import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { type ContractService, errors, paxRoute } from "../../http";
import { draftRoutes } from "./paths";
import {
  CompiledDraft,
  CreateDraftRequest,
  DraftConnection,
  DraftIdParams,
  DraftSessionState,
  RetireDraftRequest,
  SetDraftTeamRequest,
  ThawDraftRequest,
} from "./schemas";

export const service = {
  group: "drafts",
  version: "v1alpha1",
  stage: "alpha",
  title: "Pax Preset Server API",
  description:
    "Each draft's session: its world while it's being written, everyone working in it, and its freeze while it's published.",
} as const satisfies ContractService;

const serviceAuth = {
  kind: "service",
  detail: "Requires an API service access token with the drafts:manage scope.",
  caller: "api",
  scope: "drafts:manage",
} as const;
/** Every route refuses a missing or invalid credential, and one that doesn't grant the route. */
const refusals = { ...errors.unauthorized, ...errors.forbidden } as const;
const Done = z.strictObject({});

export const connect = oc
  .meta(
    paxRoute({
      auth: {
        kind: "session",
        detail:
          "Requires the browser's sign-in cookie. The preset server has the Product API confirm the account is on the preset's team and not banned from presets.",
      },
      policy: { session: true },
    }),
    openapi({
      method: "POST",
      path: draftRoutes.connect,
      operationId: "drafts.v1alpha1.connect",
      summary: "Connect to a draft.",
      description:
        "Wakes the draft's session if it was idle, and mints a short-lived draft token for the socket.",
      inputStructure: "detailed",
      successStatus: 201,
      successDescription: "A live draft token.",
    }),
  )
  .errors({
    ...errors.badRequest,
    UNAUTHORIZED: { message: "Sign in first." },
    FORBIDDEN: { message: "This account isn't on the preset's team, or is banned from presets." },
    NOT_FOUND: { message: "The draft does not exist." },
    SERVICE_UNAVAILABLE: { message: "The preset server couldn't ask the Pax API who you are." },
  })
  .input(z.object({ params: DraftIdParams }))
  .output(DraftConnection);

export const create = oc
  .meta(
    paxRoute({ auth: serviceAuth }),
    openapi({
      method: "PUT",
      path: draftRoutes.draft,
      operationId: "drafts.v1alpha1.create",
      summary: "Begin a draft session.",
      description:
        "Gives a new draft its beginning: the records a published version's draft held, or nothing. Repeating it with the same beginning changes nothing.",
      inputStructure: "detailed",
    }),
  )
  .errors({ ...errors.badRequest, ...refusals, ...errors.conflict })
  .input(z.object({ params: DraftIdParams, body: CreateDraftRequest }))
  .output(DraftSessionState);

export const setTeam = oc
  .meta(
    paxRoute({ auth: serviceAuth }),
    openapi({
      method: "PUT",
      path: draftRoutes.team,
      operationId: "drafts.v1alpha1.setTeam",
      summary: "Tell a draft who is on its team.",
      description:
        "Replaces what everyone may change in the draft. Anyone no longer on the team is closed out of it at once.",
      inputStructure: "detailed",
    }),
  )
  .errors({ ...errors.badRequest, ...refusals, ...errors.notFound })
  .input(z.object({ params: DraftIdParams, body: SetDraftTeamRequest }))
  .output(Done);

export const freeze = oc
  .meta(
    paxRoute({ auth: serviceAuth }),
    openapi({
      method: "POST",
      path: draftRoutes.freeze,
      operationId: "drafts.v1alpha1.freeze",
      summary: "Freeze a draft while it's published.",
      description:
        "Stops the draft taking changes, compiles it at its last revision into a World artifact and a records file, and stores each as a blob. Freezing a frozen draft compiles it again.",
      inputStructure: "detailed",
    }),
  )
  .errors({ ...errors.badRequest, ...refusals, ...errors.notFound, ...errors.conflict })
  .input(z.object({ params: DraftIdParams }))
  .output(CompiledDraft);

export const thaw = oc
  .meta(
    paxRoute({ auth: serviceAuth }),
    openapi({
      method: "POST",
      path: draftRoutes.thaw,
      operationId: "drafts.v1alpha1.thaw",
      summary: "Let a frozen draft take changes again.",
      description: "Tells its editors why its publish was refused or failed.",
      inputStructure: "detailed",
    }),
  )
  .errors({ ...errors.badRequest, ...refusals, ...errors.notFound })
  .input(z.object({ params: DraftIdParams, body: ThawDraftRequest }))
  .output(Done);

export const retire = oc
  .meta(
    paxRoute({ auth: serviceAuth }),
    openapi({
      method: "POST",
      path: draftRoutes.retire,
      operationId: "drafts.v1alpha1.retire",
      summary: "End a draft that was published.",
      description:
        "Tells its editors the version it became, closes their sockets, and deletes the session's storage.",
      inputStructure: "detailed",
    }),
  )
  .errors({ ...errors.badRequest, ...refusals })
  .input(z.object({ params: DraftIdParams, body: RetireDraftRequest }))
  .output(Done);

export const discard = oc
  .meta(
    paxRoute({ auth: serviceAuth }),
    openapi({
      method: "DELETE",
      path: draftRoutes.draft,
      operationId: "drafts.v1alpha1.discard",
      summary: "Discard a draft.",
      description: "Closes its editors' sockets and deletes the session's storage.",
      inputStructure: "detailed",
    }),
  )
  .errors({ ...errors.badRequest, ...refusals })
  .input(z.object({ params: DraftIdParams }))
  .output(Done);

export const exportDraft = oc
  .meta(
    paxRoute({ auth: serviceAuth }),
    openapi({
      method: "POST",
      path: draftRoutes.export,
      operationId: "drafts.v1alpha1.export",
      summary: "Compile a draft as it is now.",
      description:
        'Compiles the draft into a World artifact and a records file without freezing it, and stores both in the catalog: for moderating it before an invitation, or playing it before it\'s published. One with no start date yet starts on "Undated".',
      inputStructure: "detailed",
    }),
  )
  .errors({ ...errors.badRequest, ...refusals, ...errors.notFound, ...errors.conflict })
  .input(z.object({ params: DraftIdParams }))
  .output(CompiledDraft);
